import React, { useEffect, useMemo, useRef, useState } from 'react';

import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import cx from 'classnames';
import { ASSIGNMENT_CREATE_MODES } from 'constants/assignments';
import { MOUSE_DOWN_EVENT } from 'constants/events';
import { useAssignmentTemplates } from 'sdk';

import { ReactComponent as CheckIcon } from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/assets/check-icon.svg';
import { ReactComponent as DocumentIcon } from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/assets/document.svg';
import { ReactComponent as FilterIcon } from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/assets/filter.svg';
import { ReactComponent as SearchIcon } from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/assets/search.svg';
import ClassSelector from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/ClassSelector';
import ExemplarUpload from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/ExemplarUpload';
import RubricSelector from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/RubricSelector';
import StandardSelector from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/StandardSelector';
import TemplateFilters from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/TemplateFilters';
import {
  assignmentAssign,
  useRecentSectionsList
} from 'pages/Teachers/shared/AssignmentDialog/components/AssignAssignment/sdk';
import { VALUES_OPTIONS } from 'pages/Teachers/shared/AssignmentDialog/constants';
import { trackerCreate } from 'pages/Teachers/shared/AssignmentDialog/sdk';
import {
  buildAssignmentFormData,
  validateAssignmentForm
} from 'utils/assignments';
import { notifyError } from 'utils/notifications';

import styles from './styles.module.scss';

const MathAssignmentForm = ({ onCreationSuccess }) => {
  const [mode, setMode] = useState(ASSIGNMENT_CREATE_MODES.TEMPLATE);
  const [assignmentName, setAssignmentName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedRubric, setSelectedRubric] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const searchContainerRef = useRef(null);

  const [templateFilters, setTemplateFilters] = useState({
    is_accelerated: false,
    root: '',
    grade: '',
    unit: '',
    lesson: ''
  });

  const { data: rawSectionsResponse, isLoading: isLoadingSections } =
    useRecentSectionsList();

  const { data: rawTemplatesResponse } =
    useAssignmentTemplates(templateFilters) || {};

  const templates = useMemo(() => {
    return Array.isArray(rawTemplatesResponse)
      ? rawTemplatesResponse
      : rawTemplatesResponse?.results || [];
  }, [rawTemplatesResponse]);

  const isFormValid = validateAssignmentForm({
    mode,
    assignmentName,
    description,
    selectedSections,
    selectedRubric,
    selectedStandards,
    uploadedFile
  });

  const toggleTemplateDropdown = (e) => {
    const nextState = !showTemplateDropdown;
    setShowTemplateDropdown(nextState);
    if (nextState) {
      setShowFilters(false);
    }
  };

  const processRecentSections = ({ nestedData }) => {
    if (!Array.isArray(nestedData)) return [];

    const allSections = [];

    nestedData.forEach((subArray) => {
      if (Array.isArray(subArray)) {
        subArray.forEach((section) => {
          if (section) allSections.push(section);
        });
      }
    });

    const uniqueSections = Array.from(
      new Map(allSections.map((item) => [item.id, item])).values()
    );

    return uniqueSections;
  };

  const sections = useMemo(() => {
    const actualData = Array.isArray(rawSectionsResponse)
      ? rawSectionsResponse
      : rawSectionsResponse?.data || [];

    return processRecentSections({ nestedData: actualData });
  }, [rawSectionsResponse]);

  const handleCreateAssignment = async () => {
    setIsSubmitting(true);

    try {
      const formData = buildAssignmentFormData({
        assignmentName,
        description,
        uploadedFile,
        selectedRubric,
        selectedStandards
      });

      const {
        success: createSuccess,
        data: createData,
        errors: createErrors
      } = await trackerCreate(formData);

      if (!createSuccess) notifyError(createErrors);

      const now = new Date();
      const due = new Date();
      due.setDate(now.getDate() + 2);

      const assignmentPayload = {
        sections_data: selectedSections.map((section) => ({
          section_id: section.id,
          open: now.toISOString(),
          due: due.toISOString()
        }))
      };

      const { success: assignSuccess, errors: assignErrors } =
        await assignmentAssign(createData.id, assignmentPayload);

      if (!assignSuccess) notifyError(assignErrors);

      onCreationSuccess({ createdTrackerId: createData.id });
    } catch (error) {
      notifyError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = () => {
    setMode((prev) =>
      prev === ASSIGNMENT_CREATE_MODES.TEMPLATE
        ? ASSIGNMENT_CREATE_MODES.SCRATCH
        : ASSIGNMENT_CREATE_MODES.TEMPLATE
    );
    setSelectedTemplate(null);
    setUploadedFile(null);
  };

  const toggleFilters = (e) => {
    e.stopPropagation();
    const nextState = !showFilters;
    setShowFilters(nextState);
    if (nextState) {
      setShowTemplateDropdown(false);
    }
  };

  // TODO: save is_accelerated in local state and preserve state
  // const handleToggleAccelerated = () => {
  //   setFilters((prev) => ({
  //     ...prev,
  //     is_accelerated: !prev.is_accelerated
  //   }));
  // };

  // const handleSelectChange = (key, value) => {
  //   setFilters((prev) => ({
  //     ...prev,
  //     [key]: value
  //   }));
  // };

  const handleSelectTemplate = ({ template }) => {
    setSelectedTemplate(template);
    setAssignmentName(template.assignment_template_title);
    setShowTemplateDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setShowTemplateDropdown(false);
        setShowFilters(false);
      }
    };

    document.addEventListener(MOUSE_DOWN_EVENT, handleClickOutside);

    return () => {
      document.removeEventListener(MOUSE_DOWN_EVENT, handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.container}>
        {/* --- HEADER --- */}
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>Launch New Assignment</h3>
          </div>

          <div className={styles.assignmentTypeRow}>
            <div className={styles.typeLabel}>
              <DocumentIcon />
              <span>Assignment</span>
            </div>
            <button onClick={toggleMode} className={styles.modeToggle}>
              {mode === ASSIGNMENT_CREATE_MODES.TEMPLATE
                ? 'Create from scratch →'
                : 'Use a Template →'}
            </button>
          </div>
        </div>

        {/* --- FORM CONTENT --- */}
        <div className={styles.formContent}>
          {/* Template Search (Template Mode Only) */}
          {mode === ASSIGNMENT_CREATE_MODES.TEMPLATE && (
            <div
              className={styles.searchWrapper}
              ref={searchContainerRef}
              onClick={toggleTemplateDropdown}
            >
              <FilterIcon
                className={cx(styles.filterIcon, {
                  [styles.active]: showFilters
                })}
                onClick={toggleFilters}
              />
              <input
                type="text"
                readOnly
                placeholder="Select a Template..."
                value={selectedTemplate?.assignment_template_title || ''}
                className={styles.templateInput}
              />
              <SearchIcon className={styles.searchIcon} />

              {showTemplateDropdown && (
                <div
                  className={styles.templateFloatingList}
                  onClick={(e) => e.stopPropagation()}
                >
                  {templates.map((template) => {
                    const isSelected =
                      selectedTemplate?.assignment_template_id ===
                      template.assignment_template_id;
                    return (
                      <div
                        key={template.assignment_template_id}
                        className={cx(styles.templateItem, {
                          [styles.selected]: isSelected
                        })}
                        onClick={() => handleSelectTemplate({ template })}
                      >
                        <div className={styles.checkWrapper}>
                          {isSelected && (
                            <CheckIcon className={styles.checkIcon} />
                          )}
                        </div>

                        <div className={styles.itemContent}>
                          <div className={styles.templateItemName}>
                            {template.assignment_template_title}
                          </div>
                          <div className={styles.templateItemMeta}>
                            <span className={styles.tag}>
                              {template.root_name}
                            </span>
                            <span className={styles.tag}>
                              {template.grade_tag}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* --- FILTER DROPDOWN UI --- */}
              {showFilters && (
                <div
                  className={styles.filterFloatingPanel}
                  onClick={(e) => e.stopPropagation()}
                >
                  <TemplateFilters
                    filters={templateFilters}
                    setFilters={setTemplateFilters}
                  />
                </div>
              )}
            </div>
          )}
          <input
            type="text"
            className={styles.inputField}
            placeholder="Assignment Name"
            value={assignmentName}
            onChange={(e) => setAssignmentName(e.target.value)}
          />

          <textarea
            className={cx(styles.inputField, styles.textArea)}
            placeholder="Add a Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />

          {/* Learning Standard (Scratch Mode Only) */}
          {mode === ASSIGNMENT_CREATE_MODES.SCRATCH && (
            <StandardSelector
              selectedStandards={selectedStandards}
              setSelectedStandards={setSelectedStandards}
            />
          )}

          <ClassSelector
            sections={sections}
            selectedSections={selectedSections}
            setSelectedSections={setSelectedSections}
            isLoading={isLoadingSections}
          />

          <RubricSelector
            options={VALUES_OPTIONS}
            selected={selectedRubric}
            onSelect={setSelectedRubric}
          />

          {mode === ASSIGNMENT_CREATE_MODES.SCRATCH && (
            <ExemplarUpload file={uploadedFile} setFile={setUploadedFile} />
          )}
        </div>

        {/* --- FOOTER --- */}
        <div className={styles.footer}>
          <button
            className={styles.createButton}
            disabled={!isFormValid || isSubmitting}
            onClick={handleCreateAssignment}
          >
            {isSubmitting ? (
              'Processing...'
            ) : (
              <>
                <span></span>
                CREATE ASSIGNMENT
                <ArrowForwardIcon />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MathAssignmentForm;
